import { useEffect, useRef, type RefObject } from 'react';

/**
 * Walk upward from a sentinel element until we find the nearest scrolling
 * container.
 *
 * Why this exists:
 * - Our page layouts do not always scroll on `window`
 * - Tables often live inside nested wrappers with `overflow-y: auto`
 * - The auto-pagination logic needs to observe the actual scrollable parent,
 *   otherwise it will never fire on pages rendered inside shell/content panes
 *
 * Behavior:
 * - starts at the sentinel's parent
 * - returns the first ancestor whose computed `overflow-y` can scroll
 * - returns `null` when no scroll container is found
 *
 * We intentionally use computed style checks instead of assuming a specific
 * layout component. That keeps the hook reusable across pages that may be
 * mounted inside different shells over time.
 */
function getScrollRoot(element: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = element.parentElement;

  while (current) {
    const { overflowY } = window.getComputedStyle(current);

    if (
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflowY === 'overlay'
    ) {
      return current;
    }

    current = current.parentElement;
  }

  return null;
}

/**
 * Automatically requests the next cursor-paginated page when a sentinel near
 * the bottom of a list scrolls close to the visible end of its scroll
 * container.
 *
 * This hook is the UI-side counterpart to `usePagination(...)`:
 * - `usePagination` knows how to fetch the next cursor page
 * - `useAutoLoadMore` decides when the UI should ask for that next page
 *
 * Why this is a separate hook instead of page-local logic:
 * - Transactions and Address Book need the exact same behavior
 * - The behavior is subtle enough that duplicating it invites drift
 * - The cursor guard and reset semantics are easy to get wrong
 *
 * Required markup contract:
 * - render a small sentinel element after the list/table
 * - attach its `ref` to the `ref` argument passed here
 * - render that sentinel whenever `hasMore || isLoadingMore`
 *
 * High-level behavior:
 * - no-op if the view is inactive
 * - no-op if there is no next cursor
 * - no-op while a next page is already loading
 * - find the nearest scrollable ancestor for the sentinel
 * - when the sentinel is within 200px of the visible bottom, call `loadMore()`
 *
 * Why the hook tracks the last requested cursor:
 * - scroll events can fire many times while the sentinel remains near the
 *   bottom
 * - without a cursor guard, we could enqueue the same next page repeatedly
 * - the guard is keyed to the returned cursor, not merely a boolean, so a new
 *   page automatically becomes eligible once the backend advances pagination
 *
 * Why we clear the guard when `nextCursor` changes:
 * - a refetch/reset can recreate an older pagination position
 * - the hook must allow that cursor to be requested again after the list state
 *   has genuinely changed
 * - this avoids the stale "already requested" state that can otherwise survive
 *   across refetches
 *
 * Why we clear the guard when `isActive` becomes false:
 * - tabbed or conditionally rendered pages may pause a list without unmounting
 * - if the user leaves mid-fetch boundary conditions, returning to the page
 *   should still allow the current cursor to load
 *
 * Why we immediately run the scroll check once after mounting:
 * - some pages reopen while already scrolled near the bottom
 * - waiting for the next user scroll would make infinite loading feel broken
 * - the eager check lets the next page load as soon as the sentinel is present
 *
 * Parameters:
 * - `ref`: sentinel element rendered after the list rows
 * - `isActive`: whether this list is the currently active/visible one
 * - `hasMore`: whether the backend says another page exists
 * - `isLoadingMore`: whether the next page is already in flight
 * - `nextCursor`: cursor returned from the current last page
 * - `loadMore`: callback that appends the next page
 *
 * Note:
 * - `loadMore` should be referentially stable when passed to
 *   `useAutoLoadMore`, otherwise the effect will re-attach listeners on every
 *   render
 */
export function useAutoLoadMore(
  ref: RefObject<HTMLDivElement | null>,
  isActive: boolean,
  hasMore: boolean,
  isLoadingMore: boolean,
  nextCursor: string | null,
  loadMore: () => Promise<void>
) {
  /**
   * Remembers which cursor has already been requested while the sentinel is
   * near the bottom. This prevents duplicate `loadMore()` calls for the same
   * page boundary.
   */
  const lastRequestedCursorRef = useRef<string | null>(null);

  /**
   * Tracks the previously seen cursor so we can detect when pagination has
   * genuinely advanced or been reset.
   */
  const previousCursorRef = useRef<string | null>(null);

  useEffect(() => {
    if (nextCursor !== previousCursorRef.current) {
      // A different cursor means the list has advanced (or been reset) to a new
      // pagination boundary. The next page may now be requested again.
      lastRequestedCursorRef.current = null;
      previousCursorRef.current = nextCursor;
    }
  }, [nextCursor]);

  useEffect(() => {
    if (isActive) {
      return;
    }

    // Inactive views should not permanently "consume" the current cursor. When
    // the user returns to the page/tab, the same cursor may still need to be
    // loaded depending on how far down they already are.
    lastRequestedCursorRef.current = null;
  }, [isActive]);

  useEffect(() => {
    if (!isActive || !hasMore || isLoadingMore || !nextCursor) {
      return;
    }

    const element = ref.current;

    if (!element) {
      return;
    }

    const scrollRoot = getScrollRoot(element);

    if (!scrollRoot) {
      return;
    }

    const maybeLoadMore = () => {
      const rootRect = scrollRoot.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      // The sentinel does not need to be fully visible. We intentionally start
      // loading slightly early so the next page can arrive before the user hits
      // a hard stop at the bottom.
      const isNearBottom = elementRect.top - rootRect.bottom <= 200;

      if (!isNearBottom) {
        return;
      }

      // Guard on the cursor returned by the current last page so repeated
      // scroll/resize events do not enqueue the same page multiple times.
      if (lastRequestedCursorRef.current === nextCursor) {
        return;
      }

      lastRequestedCursorRef.current = nextCursor;
      void loadMore();
    };

    // Run once immediately so a list that is already near the bottom can keep
    // flowing without waiting for another user gesture.
    maybeLoadMore();

    scrollRoot.addEventListener('scroll', maybeLoadMore, { passive: true });
    window.addEventListener('resize', maybeLoadMore);

    return () => {
      scrollRoot.removeEventListener('scroll', maybeLoadMore);
      window.removeEventListener('resize', maybeLoadMore);
    };
  }, [hasMore, isActive, isLoadingMore, loadMore, nextCursor, ref]);
}
